import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import './AdminStats.css';

// Convert country code to flag emoji
const getCountryFlag = (countryCode) => {
  if (!countryCode) return '';
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

// Country code to name mapping
const countryNames = {
  US: 'United States', CA: 'Canada', GB: 'United Kingdom', AU: 'Australia',
  DE: 'Germany', FR: 'France', IT: 'Italy', ES: 'Spain', PT: 'Portugal',
  NL: 'Netherlands', BE: 'Belgium', CH: 'Switzerland', AT: 'Austria',
  SE: 'Sweden', NO: 'Norway', DK: 'Denmark', FI: 'Finland', PL: 'Poland',
  CZ: 'Czech Republic', RU: 'Russia', UA: 'Ukraine', BR: 'Brazil',
  MX: 'Mexico', AR: 'Argentina', CL: 'Chile', CO: 'Colombia', PE: 'Peru',
  JP: 'Japan', KR: 'South Korea', CN: 'China', IN: 'India', PH: 'Philippines',
  ID: 'Indonesia', TH: 'Thailand', VN: 'Vietnam', MY: 'Malaysia', SG: 'Singapore',
  ZA: 'South Africa', EG: 'Egypt', NG: 'Nigeria', KE: 'Kenya', MA: 'Morocco',
  TR: 'Turkey', SA: 'Saudi Arabia', AE: 'UAE', IL: 'Israel', PK: 'Pakistan',
  BD: 'Bangladesh', NZ: 'New Zealand', IE: 'Ireland', GR: 'Greece', RO: 'Romania',
  HU: 'Hungary', BG: 'Bulgaria', HR: 'Croatia', RS: 'Serbia', SK: 'Slovakia'
};

function AdminStats() {
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [isStaff, setIsStaff] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    checkStaffAndLoadStats();
  }, []);

  const checkStaffAndLoadStats = async () => {
    try {
      const userId = localStorage.getItem('userId');
      if (!userId) {
        navigate('/');
        return;
      }

      // Check if admin or worker
      const adminDoc = await getDoc(doc(db, 'admins', userId));
      if (adminDoc.exists()) {
        setIsStaff(true);
      } else {
        const workerDoc = await getDoc(doc(db, 'workers', userId));
        if (!workerDoc.exists()) {
          navigate('/');
          return;
        }
        setIsStaff(true);
      }

      // Load stats
      const statsDoc = await getDoc(doc(db, 'stats', 'messagesByCountry'));
      if (statsDoc.exists()) {
        setStats(statsDoc.data());
      }

      setLoading(false);
    } catch (error) {
      console.error('Error loading stats:', error);
      setLoading(false);
    }
  };

  // Sort countries by message count
  const sortedCountries = Object.entries(stats)
    .sort(([, a], [, b]) => b - a);

  const totalMessages = sortedCountries.reduce((sum, [, count]) => sum + count, 0);
  const maxCount = sortedCountries.length > 0 ? sortedCountries[0][1] : 0;

  if (loading) {
    return (
      <div className="admin-stats-container">
        <div className="admin-stats-card">
          <h2>Loading stats...</h2>
        </div>
      </div>
    );
  }

  if (!isStaff) {
    return (
      <div className="admin-stats-container">
        <div className="admin-stats-card">
          <h2>⚠️ Access Denied</h2>
          <p>You are not authorized to view this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-stats-container">
      <div className="admin-stats-card">
        <div className="stats-header">
          <h1>📊 Anonymous Message Stats</h1>
          <p className="stats-subtitle">Messages sent by country (anonymous conversations only)</p>
        </div>

        <div className="stats-summary">
          <div className="summary-item">
            <span className="summary-number">{totalMessages.toLocaleString()}</span>
            <span className="summary-label">Total Messages</span>
          </div>
          <div className="summary-item">
            <span className="summary-number">{sortedCountries.length}</span>
            <span className="summary-label">Countries</span>
          </div>
        </div>

        {sortedCountries.length === 0 ? (
          <div className="no-stats">
            <p>📭 No stats yet</p>
            <p className="no-stats-hint">Stats will appear here as users send anonymous messages.</p>
          </div>
        ) : (
          <div className="stats-chart">
            {sortedCountries.map(([countryCode, count], index) => {
              const percentage = maxCount > 0 ? (count / maxCount) * 100 : 0;
              const globalPercentage = totalMessages > 0 ? ((count / totalMessages) * 100).toFixed(1) : 0;

              return (
                <div key={countryCode} className="chart-row">
                  <div className="chart-rank">#{index + 1}</div>
                  <div className="chart-country">
                    <span className="country-flag">{getCountryFlag(countryCode)}</span>
                    <span className="country-name">{countryNames[countryCode] || countryCode}</span>
                  </div>
                  <div className="chart-bar-container">
                    <div
                      className="chart-bar"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <div className="chart-count">
                    <span className="count-number">{count.toLocaleString()}</span>
                    <span className="count-percentage">({globalPercentage}%)</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <button className="back-button" onClick={() => navigate('/admin-management')}>
          ← Back to Admin Panel
        </button>
      </div>
    </div>
  );
}

export default AdminStats;
